/**
 * Navigation model for the portal.
 *
 * Kept OUT of Sidebar.tsx so that file exports nothing but its component (Vite fast refresh
 * stops working for a module that mixes components with shared constants). Both the sidebar and
 * MainLayout's AppBar-title fallback read this one structure, so a new page is added in exactly
 * one place.
 */

import { ReactNode } from 'react'
import {
  AdminPanelSettings,
  Groups,
  Article,
  People,
  Explore,
  BarChart,
  Palette,
  EmojiEvents,
  Build,
  Map,
  Tune,
  ConfirmationNumber,
  RateReview,
  Gavel,
  Visibility,
  Block,
  Speed,
  CardMembership,
  Storage,
  BugReport,
  AutoStories,
  RecordVoiceOver,
} from '@mui/icons-material'

export interface NavItem {
  label: string
  path: string
  icon: ReactNode
  requiresAuth?: boolean
  requiresAdmin?: boolean
}

export interface NavSection {
  /** Stable id, used as the React key. */
  key: string
  /** Section heading. Omit for the top group, which needs no label. */
  label?: string
  items: NavItem[]
}

/**
 * The portal's navigation, grouped by what an operator is trying to DO rather than as one flat
 * list. Everything that renders the nav — the sidebar itself, and MainLayout's AppBar title
 * fallback — reads this one structure, so a new page is added in exactly one place.
 */
export const NAV_SECTIONS: NavSection[] = [
  {
    key: 'overview',
    items: [{ label: 'Dashboard', path: '/', icon: <AdminPanelSettings />, requiresAdmin: true }],
  },
  {
    key: 'manage',
    label: 'Manage',
    items: [
      { label: 'Users', path: '/users', icon: <People />, requiresAdmin: true },
      { label: 'Groups', path: '/groups', icon: <Groups />, requiresAdmin: true },
      { label: 'Adventures', path: '/adventures', icon: <Explore />, requiresAdmin: true },
      { label: 'Badges', path: '/badges', icon: <EmojiEvents />, requiresAdmin: true },
      { label: 'Themes', path: '/themes', icon: <Palette />, requiresAdmin: true },
      // Last in the group: plans are configuration, not a list worked through daily like the
      // ones above. Subscription levels for members and businesses. Putting one user on a plan is on
      // that user's Subscription tab, not here.
      { label: 'Plans', path: '/plans', icon: <CardMembership />, requiresAdmin: true },
      // AI narration (Backlog 063): the voices and emotive tags creators may use. Configuration
      // like Plans, which holds who may generate and how much.
      { label: 'Narration', path: '/narration', icon: <RecordVoiceOver />, requiresAdmin: true },
    ],
  },
  {
    key: 'insights',
    label: 'Insights',
    items: [
      { label: 'Statistics', path: '/statistics', icon: <BarChart />, requiresAdmin: true },
      { label: 'Marketing', path: '/marketing', icon: <Map />, requiresAdmin: true },
    ],
  },
  {
    key: 'moderation',
    label: 'Moderation',
    items: [
      // Ticket statistics is a tab on this page, not a separate nav entry.
      { label: 'Tickets', path: '/tickets', icon: <ConfirmationNumber />, requiresAdmin: true },
      { label: 'Suggestions', path: '/contributions', icon: <RateReview />, requiresAdmin: true },
      // Every Story incl. hidden: Featured rank (Admin, editorial only), hide/unhide, reviews (Backlog 060).
      { label: 'Stories', path: '/stories', icon: <AutoStories />, requiresAdmin: true },
    ],
  },
  {
    key: 'system',
    label: 'System',
    items: [
      { label: 'Scoring', path: '/scoring', icon: <Tune />, requiresAdmin: true },
      // Publishing here gates the entire app, so it lives with the operational tools rather
      // than among the content screens.
      { label: 'NDA', path: '/nda', icon: <Gavel />, requiresAdmin: true },
      { label: 'Tools', path: '/tools', icon: <Build />, requiresAdmin: true },
      { label: 'IP bans', path: '/ip-bans', icon: <Block />, requiresAdmin: true },
      // Under System rather than Insights: Insights is about the catalogue and the players,
      // this is about whether the servers are keeping up.
      { label: 'Performance', path: '/performance', icon: <Speed />, requiresAdmin: true },
      // Crash reports sent by the mobile app, grouped by fingerprint.
      { label: 'Crashes', path: '/crashes', icon: <BugReport />, requiresAdmin: true },
      // Read-only: which media files nothing uses and which bucket objects nothing tracks.
      { label: 'Storage', path: '/storage', icon: <Storage />, requiresAdmin: true },
      { label: 'Diagnostics', path: '/diagnostics', icon: <Visibility />, requiresAdmin: true },
      { label: 'Logs', path: '/logs', icon: <Article />, requiresAdmin: true },
    ],
  },
]

/** Flat view of every nav destination, in sidebar order. */
export const NAV_ITEMS: NavItem[] = NAV_SECTIONS.flatMap((section) => section.items)

/**
 * The nav label that owns a pathname, by longest matching prefix ('/tickets/42' -> 'Tickets').
 * MainLayout uses it as the AppBar title for pages that never call usePageTitle, which is why
 * /tickets used to read "Dashboard".
 */
export function getNavTitleForPath(pathname: string): string | undefined {
  if (pathname === '/') {
    return NAV_ITEMS.find((item) => item.path === '/')?.label
  }

  let best: NavItem | undefined
  for (const item of NAV_ITEMS) {
    if (item.path === '/') continue
    if (pathname === item.path || pathname.startsWith(`${item.path}/`)) {
      if (!best || item.path.length > best.path.length) best = item
    }
  }
  return best?.label
}
